export const RATE_LIMIT_MAX_RETRIES = 3;
export const RATE_LIMIT_BASE_DELAY_MS = 1000;
export const RATE_LIMIT_MAX_DELAY_MS = 16_000;

export type RateLimitRetryConfig = {
  rateLimitRetryCount?: number;
};

export function getRetryAfterHeader(headers: unknown): string | number | undefined {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  if ('get' in headers && typeof (headers as { get?: unknown }).get === 'function') {
    const value = (headers as { get: (name: string) => unknown }).get('retry-after');
    return typeof value === 'string' || typeof value === 'number' ? value : undefined;
  }
  const value = (headers as Record<string, unknown>)['retry-after'];
  return typeof value === 'string' || typeof value === 'number' ? value : undefined;
}

export function parseRetryAfterMs(retryAfter: string | number | undefined): number | undefined {
  if (retryAfter === undefined || retryAfter === '') {
    return undefined;
  }
  const asNumber = Number(retryAfter);
  if (Number.isFinite(asNumber)) {
    return Math.max(0, asNumber * 1000);
  }
  const asDate = Date.parse(String(retryAfter));
  if (Number.isNaN(asDate)) {
    return undefined;
  }
  return Math.max(0, asDate - Date.now());
}

export function getRateLimitRetryDelayMs({
  retryAfterHeader,
  attempt,
  random = Math.random
}: {
  retryAfterHeader?: string | number;
  attempt: number;
  random?: () => number;
}): number {
  const fromHeader = parseRetryAfterMs(retryAfterHeader);
  if (fromHeader !== undefined) {
    return Math.min(fromHeader, RATE_LIMIT_MAX_DELAY_MS);
  }
  const exp = Math.min(RATE_LIMIT_BASE_DELAY_MS * 2 ** attempt, RATE_LIMIT_MAX_DELAY_MS);
  const jitter = random() * RATE_LIMIT_BASE_DELAY_MS;
  return Math.min(exp + jitter, RATE_LIMIT_MAX_DELAY_MS);
}

export function shouldRetryRateLimit({ status, retryCount }: { status?: number; retryCount: number }): boolean {
  return status === 429 && retryCount < RATE_LIMIT_MAX_RETRIES;
}
